import { useEffect, useMemo, useState } from "react";
import { Save } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../../hooks/useAuth";
import { presetsApi, type PresetResponse } from "../../lib/presets";
import { areasApi } from "../../lib/areas";
import { sitesApi } from "../../lib/sites";
import {
  apiWindowsToAvailabilitySlots,
  availabilitySlotsToApiPayload,
  type AvailabilitySlot,
} from "../../lib/weekly-availability";
import { lightPresetGroupsApi } from "../../lib/light-preset-groups";
import { canEditAlias } from "../../utils/role-utils";

import { WeeklyAvailabilityCalendar } from "@/components/scheduling/WeeklyAvailabilityCalendar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

interface LightPresetGroupFormModalProps {
  /** ID of the `light_preset_groups` association row. */
  lightPresetGroupId: string;
  /** The light preset this association is for (display only). */
  preset: PresetResponse;
  /** Optional site id for loading site curfew background. */
  siteId?: string;
  onClose: () => void;
}

function axiosErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e.response?.data?.error ?? e.message ?? "Request failed";
}

export default function LightPresetGroupFormModal({
  lightPresetGroupId,
  preset,
  siteId,
  onClose,
}: LightPresetGroupFormModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canManageSchedule = user?.role === "super_user" || user?.role === "site_admin";
  const canEditAliasField = canEditAlias(user?.role);

  const { data: area } = useQuery({
    queryKey: ["area", preset.areaId],
    queryFn: () => areasApi.getById(preset.areaId),
    enabled: !!preset.areaId,
  });

  const { data: site } = useQuery({
    queryKey: ["site", siteId],
    queryFn: () => sitesApi.getById(siteId!),
    enabled: !!siteId && canManageSchedule,
  });

  const { data: availabilityRows } = useQuery({
    queryKey: ["light-preset-group-available-times", lightPresetGroupId],
    queryFn: () => lightPresetGroupsApi.getAvailableTimes(lightPresetGroupId),
    enabled: !!lightPresetGroupId && canManageSchedule,
  });

  const { data: lpgRow } = useQuery({
    queryKey: ["light-preset-group", lightPresetGroupId],
    queryFn: () => lightPresetGroupsApi.getById(lightPresetGroupId),
    enabled: !!lightPresetGroupId && canManageSchedule,
  });

  const [availabilitySlots, setAvailabilitySlots] = useState<AvailabilitySlot[]>([]);
  const [alias, setAlias] = useState("");
  const [overridePresetAvailableTimes, setOverridePresetAvailableTimes] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (availabilityRows === undefined) return;
    if (!overridePresetAvailableTimes) return;
    setAvailabilitySlots(apiWindowsToAvailabilitySlots(availabilityRows));
  }, [availabilityRows, overridePresetAvailableTimes]);

  useEffect(() => {
    if (lpgRow !== undefined) {
      setAlias(lpgRow.alias ?? "");
      setOverridePresetAvailableTimes(
        lpgRow.overridePresetAvailableTimes ?? false,
      );
    }
  }, [lpgRow]);

  const { data: presetAvailabilityRows, isPending: presetAvailabilityPending } =
    useQuery({
      queryKey: ["preset-available-times", preset.id],
      queryFn: () => presetsApi.getAvailableTimes(preset.id),
      enabled: !!preset.id && canManageSchedule,
    });

  const presetAvailabilitySlots = useMemo(() => {
    if (presetAvailabilityRows === undefined) return [];
    return apiWindowsToAvailabilitySlots(presetAvailabilityRows);
  }, [presetAvailabilityRows]);

  const title = useMemo(() => {
    return `User Group Preset Alias`;
  }, []);

  const disabled = !canManageSchedule || isSaving;

  const handleSave = async () => {
    setSaveError(null);
    setIsSaving(true);
    try {
      await lightPresetGroupsApi.patch(lightPresetGroupId, {
        ...(canEditAliasField
          ? { alias: alias.trim() ? alias.trim() : null }
          : {}),
        overridePresetAvailableTimes,
      });
      if (overridePresetAvailableTimes) {
        await lightPresetGroupsApi.putAvailableTimes(
          lightPresetGroupId,
          availabilitySlotsToApiPayload(availabilitySlots),
        );
      }
      await queryClient.invalidateQueries({
        queryKey: ["light-preset-group-available-times", lightPresetGroupId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["light-preset-group", lightPresetGroupId],
      });
      if (siteId) {
        await queryClient.invalidateQueries({
          queryKey: ["light-presets", siteId],
        });
      }
      onClose();
    } catch (err) {
      setSaveError(axiosErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[980px] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-sm text-muted-foreground">Preset</Label>
            <div className="text-sm font-medium">
              {preset.name}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lpg-alias">Alias</Label>
            <Input
              id="lpg-alias"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="Dashboard alias"
              disabled={disabled || !canEditAliasField}
              readOnly={!canEditAliasField}
            />
            <p className="text-xs text-muted-foreground">
              {canEditAliasField
                ? "Optional. This alias controls how the preset is labeled for this user group."
                : "Only Super Users can edit the dashboard alias. You can still configure weekly availability below."}
            </p>
          </div>

          <Card className="p-3">
            <div className="space-y-2">
              <Label className="text-base">Weekly available times</Label>
              <div className="flex items-start gap-2 rounded-md border border-border p-3">
                <Checkbox
                  id="lpg-override-preset-times"
                  checked={overridePresetAvailableTimes}
                  onCheckedChange={(v) => {
                    const on = v === true;
                    setOverridePresetAvailableTimes(on);
                    if (!on) setAvailabilitySlots([]);
                  }}
                  disabled={disabled}
                  className="mt-0.5"
                />
                <div className="min-w-0 space-y-1">
                  <Label
                    htmlFor="lpg-override-preset-times"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Override preset schedule for this group
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, the calendar below replaces the preset’s weekly
                    windows for members of this group. When disabled, the preset’s
                    own schedule applies.
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {overridePresetAvailableTimes
                  ? "Edit the calendar to set group-specific windows (used only when override is enabled)."
                  : "Showing this area preset’s weekly windows. Turn on override above to set group-specific times."}
              </p>
              {!overridePresetAvailableTimes && presetAvailabilityPending && (
                <p className="text-xs text-muted-foreground">Loading preset schedule…</p>
              )}
              <WeeklyAvailabilityCalendar
                siteCurfew={{ start: site?.curfewStart, end: site?.curfewEnd }}
                areaCurfew={{ start: area?.curfewStart, end: area?.curfewEnd }}
                value={
                  availabilitySlots
                }
                templateAvailability={presetAvailabilitySlots}
                dimTemplateAvailability={overridePresetAvailableTimes}
                onChange={setAvailabilitySlots}
                disabled={disabled || !overridePresetAvailableTimes}
                fillAction={
                  overridePresetAvailableTimes
                    ? {
                        label: "Initialise with preset availability",
                        onClick: () => {
                          if (presetAvailabilityRows === undefined) return;
                          setAvailabilitySlots(presetAvailabilitySlots);
                        },
                      }
                    : undefined
                }
                idPrefix={`light-preset-group-availability-${lightPresetGroupId}`}
              />
            </div>
          </Card>

          {saveError && (
            <p className="text-sm text-destructive" role="alert">
              {saveError}
            </p>
          )}

          {!canManageSchedule && (
            <p className="text-xs text-muted-foreground">
              Weekly availability is only editable for site admins and super users.
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={disabled}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

